import { useState, useEffect } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Menu, X, Sparkles } from "lucide-react";

const NAV = [
  { to: "/how-it-works", label: "How It Works" },
  { to: "/reports", label: "Reports" },
  { to: "/pricing", label: "Pricing" },
  { to: "/sample-report", label: "Sample" },
  { to: "/name-numerology", label: "Name Numerology" },
  { to: "/faq", label: "FAQ" },
  { to: "/track-order", label: "Track Order" },
];

const FOOTER_LEGAL = [
  { to: "/privacy", label: "Privacy Policy" },
  { to: "/terms", label: "Terms & Conditions" },
  { to: "/refund-policy", label: "Refund & Cancellation" },
  { to: "/disclaimer", label: "Numerology Disclaimer" },
];

export function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();
  useEffect(() => setOpen(false), [loc.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 backdrop-blur-xl bg-ivory/85 border-b transition-all ${scrolled ? "border-border shadow-sm" : "border-transparent"}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 flex items-center justify-between h-16 lg:h-20">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2 group">
          <Sparkles className="w-5 h-5 text-gold" strokeWidth={1.6} />
          <span className="font-serif text-2xl lg:text-3xl tracking-tight text-navy">Aetheria</span>
        </Link>
        <nav className="hidden lg:flex items-center gap-7">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.to.replace(/\//g, "")}`}
              className={({ isActive }) =>
                `text-sm font-medium link-underline transition-colors ${isActive ? "text-gold-hover" : "text-slate-600 hover:text-navy"}`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="hidden lg:block">
          <Link to="/pricing" data-testid="header-cta" className="btn-primary text-sm">Get Your Report</Link>
        </div>
        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden p-2 text-navy"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>
      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-ivory border-t border-border px-4 py-4 space-y-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`mobile-nav-${n.to.replace(/\//g, "")}`}
              className="block py-2.5 text-slate-700 font-medium"
            >
              {n.label}
            </NavLink>
          ))}
          <Link to="/pricing" data-testid="mobile-header-cta" className="btn-primary w-full mt-3">Get Your Report</Link>
        </div>
      )}
    </header>
  );
}

export function Footer() {
  return (
    <footer data-testid="site-footer" className="bg-navy text-ivory mt-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-16 grid grid-cols-1 md:grid-cols-4 gap-10">
        <div className="md:col-span-2">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-5 h-5 text-gold" strokeWidth={1.6} />
            <span className="font-serif text-2xl">Aetheria Numerology</span>
          </div>
          <p className="text-slate-300 text-sm leading-relaxed max-w-md">
            Personalized numerology readings, thoughtfully crafted from your details using the
            Pythagorean and Chaldean traditions. Offered for personal reflection and self-discovery —
            a beautiful report, made just for you.
          </p>
        </div>
        <div>
          <h4 className="eyebrow text-gold mb-4">Explore</h4>
          <ul className="space-y-2.5 text-sm text-slate-300">
            <li><Link to="/how-it-works" className="hover:text-gold">How It Works</Link></li>
            <li><Link to="/pricing" className="hover:text-gold">Pricing</Link></li>
            <li><Link to="/sample-report" className="hover:text-gold">Sample Report</Link></li>
            <li><Link to="/about" className="hover:text-gold">About Us</Link></li>
            <li><Link to="/contact" className="hover:text-gold">Contact & Support</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="eyebrow text-gold mb-4">Legal</h4>
          <ul className="space-y-2.5 text-sm text-slate-300">
            {FOOTER_LEGAL.map((l) => (
              <li key={l.to}><Link to={l.to} className="hover:text-gold">{l.label}</Link></li>
            ))}
            <li><Link to="/track-order" className="hover:text-gold">Order Status</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-6 flex flex-col sm:flex-row justify-between gap-3 text-xs text-slate-400">
          <span>© {new Date().getFullYear()} Aetheria Numerology. All rights reserved.</span>
          <Link to="/admin/login" data-testid="admin-portal-link" className="hover:text-gold">Admin Portal</Link>
        </div>
      </div>
    </footer>
  );
}

export default function PublicLayout() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}
